/**
 * Utility functions for visualization configuration and data processing
 */

/**
 * Detects the data type of a column based on sample data
 * @param {Array} data - Array of data objects
 * @param {string} columnName - Name of the column to analyze
 * @returns {string} - 'numeric', 'text', 'date', or 'unknown'
 */
export const detectColumnType = (data, columnName) => {
  if (!data || data.length === 0 || !columnName) return 'unknown';

  // Sample first few non-null values
  const samples = data
    .map(row => row[columnName])
    .filter(value => value !== null && value !== undefined && value !== '')
    .slice(0, 10);

  if (samples.length === 0) return 'unknown';

  // Check if all samples are numbers
  const numericCount = samples.filter(value => 
    typeof value === 'number' || (!isNaN(Number(value)) && !isNaN(parseFloat(value)))
  ).length;

  // Check if all samples are dates
  const dateCount = samples.filter(value => {
    const date = new Date(value);
    return date instanceof Date && !isNaN(date.getTime());
  }).length;

  // Determine type based on majority
  const threshold = samples.length * 0.7; // 70% threshold

  if (numericCount >= threshold) return 'numeric';
  if (dateCount >= threshold) return 'date';
  return 'text';
};

/**
 * Generates smart axis labels based on column names and data types
 * @param {string} columnName - Name of the column
 * @param {string} columnType - Type of the column ('numeric', 'text', 'date')
 * @param {string} axisType - Type of axis ('x' or 'y')
 * @returns {string} - Generated axis label
 */
export const generateSmartAxisLabel = (columnName, columnType, axisType) => {
  if (!columnName) return '';

  // Common patterns for better labels
  const labelMappings = {
    // User-related
    'user_id': 'User ID',
    'userid': 'User ID',
    'userId': 'User ID',
    'user': 'User',
    'username': 'Username',
    'name': 'Name',
    
    // Time-related
    'time': 'Time',
    'timestamp': 'Timestamp',
    'date': 'Date',
    'created_at': 'Created Date',
    'createdAt': 'Created Date',
    'updated_at': 'Updated Date',
    'updatedAt': 'Updated Date',
    'start_time': 'Start Time',
    'startTime': 'Start Time',
    'end_time': 'End Time',
    'endTime': 'End Time',
    
    // Duration/Time measurements
    'duration': 'Duration (ms)',
    'duration_ms': 'Duration (ms)',
    'durationMs': 'Duration (ms)',
    'duration_seconds': 'Duration (seconds)',
    'durationSeconds': 'Duration (seconds)',
    'duration_minutes': 'Duration (minutes)',
    'durationMinutes': 'Duration (minutes)',
    'duration_hours': 'Duration (hours)',
    'durationHours': 'Duration (hours)',
    
    // Count/Quantity
    'count': 'Count',
    'total': 'Total',
    'sum': 'Sum',
    'amount': 'Amount',
    'value': 'Value',
    'quantity': 'Quantity',
    'number': 'Number',
    
    // Categories
    'category': 'Category',
    'type': 'Type',
    'status': 'Status',
    'state': 'State',
    'level': 'Level',
    'grade': 'Grade',
    'class': 'Class',
    
    // Performance metrics
    'performance': 'Performance',
    'score': 'Score',
    'rating': 'Rating',
    'efficiency': 'Efficiency',
    'accuracy': 'Accuracy',
    'speed': 'Speed',
    'rate': 'Rate',
    
    // Financial
    'price': 'Price',
    'cost': 'Cost',
    'revenue': 'Revenue',
    'profit': 'Profit',
    'income': 'Income',
    'expense': 'Expense',
    'budget': 'Budget',
    
    // Technical
    'size': 'Size',
    'length': 'Length',
    'width': 'Width',
    'height': 'Height',
    'weight': 'Weight',
    'volume': 'Volume',
    'capacity': 'Capacity',
    
    // Geographic
    'latitude': 'Latitude',
    'longitude': 'Longitude',
    'location': 'Location',
    'address': 'Address',
    'city': 'City',
    'country': 'Country',
    'region': 'Region',
    
    // Network/Technical
    'ip': 'IP Address',
    'ip_address': 'IP Address',
    'ipAddress': 'IP Address',
    'port': 'Port',
    'protocol': 'Protocol',
    'version': 'Version',
    'url': 'URL',
    'domain': 'Domain',
    
    // Database
    'id': 'ID',
    'key': 'Key',
    'hash': 'Hash',
    'token': 'Token',
    'session': 'Session',
    'request': 'Request',
    'response': 'Response'
  };

  // Check for exact matches first
  const lowerColumnName = columnName.toLowerCase();
  if (labelMappings[lowerColumnName]) {
    return labelMappings[lowerColumnName];
  }

  // Check for partial matches
  for (const [pattern, label] of Object.entries(labelMappings)) {
    if (lowerColumnName.includes(pattern)) {
      return label;
    }
  }

  // Generate label based on column type and axis type
  if (columnType === 'date') {
    return axisType === 'x' ? 'Time' : 'Date';
  }
  
  if (columnType === 'numeric') {
    if (axisType === 'y') {
      // Common numeric patterns for Y-axis
      if (lowerColumnName.includes('count') || lowerColumnName.includes('total') || lowerColumnName.includes('sum')) {
        return 'Count';
      }
      if (lowerColumnName.includes('duration') || lowerColumnName.includes('time')) {
        return 'Duration';
      }
      if (lowerColumnName.includes('price') || lowerColumnName.includes('cost') || lowerColumnName.includes('amount')) {
        return 'Amount';
      }
      if (lowerColumnName.includes('rate') || lowerColumnName.includes('percentage') || lowerColumnName.includes('percent')) {
        return 'Rate (%)';
      }
      return 'Value';
    } else {
      return 'Category';
    }
  }
  
  if (columnType === 'text') {
    if (axisType === 'x') {
      return 'Category';
    } else {
      return 'Value';
    }
  }

  // Fallback: format column name nicely
  return columnName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
    .trim();
};

/**
 * Gets smart column suggestions for a visualization type
 * @param {Array} columns - Available column names
 * @param {Array} data - Sample data for type detection
 * @param {string} visualizationType - Type of visualization
 * @returns {Object} - Suggested configuration
 */
export const getSmartColumnSuggestions = (columns, data, visualizationType) => {
  if (!columns || columns.length === 0) return {};

  const columnTypes = {};
  columns.forEach(col => {
    columnTypes[col] = detectColumnType(data, col);
  });

  const numericColumns = columns.filter(col => columnTypes[col] === 'numeric');
  const textColumns = columns.filter(col => columnTypes[col] === 'text');
  const dateColumns = columns.filter(col => columnTypes[col] === 'date');

  switch (visualizationType) {
    case 'Line Chart':
      const lineXAxis = dateColumns[0] || textColumns[0] || columns[0];
      const lineYAxis = numericColumns[0] || columns[1] || columns[0];
      return {
        xAxis: lineXAxis,
        yAxis: lineYAxis,
        xAxisLabel: generateSmartAxisLabel(lineXAxis, columnTypes[lineXAxis], 'x'),
        yAxisLabel: generateSmartAxisLabel(lineYAxis, columnTypes[lineYAxis], 'y'),
        suggestedYColumns: numericColumns.slice(0, 5)
      };

    case 'Bar Chart':
      const barXAxis = textColumns[0] || dateColumns[0] || columns[0];
      const barYAxis = numericColumns[0] || columns[1] || columns[0];
      return {
        xAxis: barXAxis,
        yAxis: barYAxis,
        xAxisLabel: generateSmartAxisLabel(barXAxis, columnTypes[barXAxis], 'x'),
        yAxisLabel: generateSmartAxisLabel(barYAxis, columnTypes[barYAxis], 'y'),
        suggestedYColumns: numericColumns.slice(0, 5)
      };

    case 'Pie Chart':
      return {
        nameField: textColumns[0] || columns[0],
        valueField: numericColumns[0] || columns[1] || columns[0],
        suggestedNameFields: textColumns.slice(0, 3),
        suggestedValueFields: numericColumns.slice(0, 3)
      };

    case 'KPI':
      return {
        valueColumns: numericColumns.slice(0, 6) || columns.slice(0, 6),
        suggestedValueColumns: numericColumns
      };

    default:
      return {};
  }
};

/**
 * Generates a color palette for visualizations
 * @param {number} count - Number of colors needed
 * @param {string} baseColor - Base color for the palette (hex)
 * @returns {Array} - Array of hex color strings
 */
export const generateColorPalette = (count, baseColor = '#1d0c46') => {
  const defaultPalette = [
    '#1d0c46', // Primary purple
    '#caa968', // Gold accent
    '#8b5cf6', // Purple
    '#10b981', // Green
    '#f59e0b', // Orange
    '#ef4444', // Red
    '#3b82f6', // Blue
    '#8b5cf6', // Purple
    '#06b6d4', // Cyan
    '#84cc16'  // Lime
  ];

  if (count <= defaultPalette.length) {
    return defaultPalette.slice(0, count);
  }

  // Generate additional colors by varying hue
  const colors = [...defaultPalette];
  const baseHue = hexToHsl(baseColor).h;
  
  for (let i = defaultPalette.length; i < count; i++) {
    const hue = (baseHue + (i * 360 / count)) % 360;
    colors.push(hslToHex(hue, 70, 50));
  }

  return colors;
};

/**
 * Converts hex color to HSL
 * @param {string} hex - Hex color string
 * @returns {Object} - HSL values {h, s, l}
 */
export const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l;

  l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converts HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} - Hex color string
 */
export const hslToHex = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Validates visualization configuration
 * @param {string} visualizationType - Type of visualization
 * @param {Object} config - Configuration object to validate
 * @param {Array} availableColumns - Available column names
 * @returns {Object} - Validation result {isValid, errors}
 */
export const validateVisualizationConfig = (visualizationType, config, availableColumns) => {
  const errors = [];

  if (!config || typeof config !== 'object') {
    return { isValid: false, errors: ['Configuration is required'] };
  }

  switch (visualizationType) {
    case 'Line Chart':
      if (!config.xAxis || !availableColumns.includes(config.xAxis)) {
        errors.push('Valid X-axis column is required');
      }
      if (!config.yAxis || !availableColumns.includes(config.yAxis)) {
        errors.push('Valid Y-axis column is required');
      }
      break;

    case 'Bar Chart':
      if (!config.xAxis || !availableColumns.includes(config.xAxis)) {
        errors.push('Valid X-axis column is required');
      }
      if (!config.yAxis || !availableColumns.includes(config.yAxis)) {
        errors.push('Valid Y-axis column is required');
      }
      break;

    case 'Pie Chart':
      if (!config.nameField || !availableColumns.includes(config.nameField)) {
        errors.push('Valid name field is required');
      }
      if (!config.valueField || !availableColumns.includes(config.valueField)) {
        errors.push('Valid value field is required');
      }
      break;

    case 'KPI':
      if (!config.valueColumns || !Array.isArray(config.valueColumns) || config.valueColumns.length === 0) {
        errors.push('At least one value column is required');
      } else {
        const invalidColumns = config.valueColumns.filter(col => !availableColumns.includes(col));
        if (invalidColumns.length > 0) {
          errors.push(`Invalid value columns: ${invalidColumns.join(', ')}`);
        }
      }
      break;

    default:
      errors.push('Unsupported visualization type');
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Formats data for better visualization display
 * @param {Array} data - Raw data array
 * @param {Object} config - Visualization configuration
 * @returns {Array} - Formatted data array
 */
export const formatDataForVisualization = (data, config) => {
  if (!data || !Array.isArray(data) || data.length === 0) return data;

  return data.map(row => {
    const formattedRow = { ...row };
    
    // Format numeric values
    Object.keys(formattedRow).forEach(key => {
      const value = formattedRow[key];
      
      // Format large numbers
      if (typeof value === 'number' && value > 1000) {
        formattedRow[`${key}_formatted`] = value.toLocaleString();
      }
      
      // Format dates
      if (value && typeof value === 'string') {
        const date = new Date(value);
        if (date instanceof Date && !isNaN(date.getTime())) {
          formattedRow[`${key}_formatted`] = date.toLocaleDateString();
        }
      }
    });

    return formattedRow;
  });
};

/**
 * Default configuration templates for each visualization type
 */
export const DEFAULT_CONFIGS = {
  'Line Chart': {
    lineType: 'monotone',
    strokeWidth: 2,
    showGrid: true,
    showLegend: true,
    colors: ['#1d0c46', '#caa968', '#8b5cf6', '#10b981', '#f59e0b'],
    xAxisLabel: '',
    yAxisLabel: ''
  },
  'Bar Chart': {
    barRadius: 4,
    showGrid: true,
    showLegend: true,
    colors: ['#1d0c46', '#caa968', '#8b5cf6', '#10b981', '#f59e0b'],
    xAxisLabel: '',
    yAxisLabel: ''
  },
  'Pie Chart': {
    nameField: '',
    valueField: '',
    innerRadius: 0,
    outerRadius: 80,
    showLabels: true,
    showPercentages: true,
    colors: ['#1d0c46', '#caa968', '#8b5cf6', '#10b981', '#f59e0b']
  },
  'KPI': {
    layout: 'horizontal',
    showTrend: false,
    colors: ['#1d0c46'],
    valueColumns: [],
    customLabels: {}
  },
  'Table': {
    tableName: '',
    // Array of { key: originalColumn, label: displayName, color: hex }
    columns: [],
    headerColor: '#ffffff',
    headerTextcolor: '#0f172a',
    zebraStripes: false,
    pageSize: 10
  }
};
